import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import Table from "../../../../Components/UiComponents/Table/Table";
import { facilityTableData } from "../../../../JsonData/CreateFacilityConfig";
import { postDataWithBody } from "../../../../Services/Services";
import {
  getMyTaskListURL,
  completeNotificationSop,
  completeMytask,
} from "../../../../Services/EndPoints";
import Moment from "react-moment";
import { toast, ToastContainer } from "react-toastify";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import { Modal } from "react-bootstrap";
import TaskModalWithCommentSop from "../../../../Components/UiComponents/TaskModalWithComment/TaskModalWithCommentSop";
import { Paperclip } from 'lucide-react';

/**
 * MyTaskList Component
 * Displays SOP-related tasks with status management, file attachments, and priority indicators
 * Supports both notification and activity type tasks
 */
const MyTaskList = forwardRef((props, ref) => {
  // Core state management
  const [data, setData] = useState([]); // Task list data
  const [rowCount, setRowCount] = useState(0); // Total number of tasks
  const [taskData, setTaskData] = useState(null); // Selected task for modal
  const [showTaskModal, setShowTaskModal] = useState(false); // Task completion modal visibility
  const [showImageModal, setShowImageModal] = useState(false); // Image preview modal visibility
  const [imageSrc, setImageSrc] = useState(""); // Selected image source

  // Hooks
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();

  // Fetch tasks on component mount with delay
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTaskList();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  /**
   * Fetches the SOP task list from the API
   */
  const fetchTaskList = async () => {
    try {
      const response = await postDataWithBody(
        getMyTaskListURL,
        { user_name: getLoggedInUserName() },
        {}
      );
      
      if (response) {
        setRowCount(response.length);
        setData(response);
      }
    } catch (error) {
      console.error("Error fetching SOP task list:", error);
      toast.error("Failed to fetch tasks", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  /**
   * Expose refresh function to parent component
   */
  useImperativeHandle(ref, () => ({
    refreshSopList: fetchTaskList
  }));

  /**
   * Get logged in user's email from Keycloak token
   */
  const getLoggedInUserName = () => {
    return keycloak.idTokenParsed?.email || '';
  };

  /**
   * Show task completion modal for activity tasks
   */
  const showTaskCompletionModal = (taskItem) => {
    setTaskData(taskItem);
    setShowTaskModal(true);
  };

  /**
   * Handle image attachment click - opens image preview modal
   */
  const handleImageClick = (src) => {
    setImageSrc(src);
    setShowImageModal(true);
  };

  /**
   * Close image preview modal
   */
  const handleImageModalClose = () => {
    setShowImageModal(false);
    setImageSrc("");
  };

  /**
   * Refresh table and close modal after task completion
   */
  const handleTaskCompletion = () => {
    fetchTaskList();
    setShowTaskModal(false);
  };

  /**
   * Component for handling task status changes
   */
  const TaskStatusCell = ({ row }) => {
    const [showNotificationConfirm, setShowNotificationConfirm] = useState(false);
    const [showActivityConfirm, setShowActivityConfirm] = useState(false);
    
    const { task_status_code: status, task_type, id, task_id } = row.original;

    /**
     * Handle status change selection
     */
    const handleStatusChange = (newStatus) => {
      if (newStatus === "TASK_EXE_STATUS_INPROGRESS") {
        if (task_type === "Activity") {
          showTaskCompletionModal(row.original);
        } else {
          setShowNotificationConfirm(true);
        }
      } else if (newStatus === "TASK_EXE_STATUS_COMPLETED") {
        if (task_type === "Notification") {
          setShowNotificationConfirm(true);
        } else {
          showTaskCompletionModal(row.original);
        }
      }
    };

    /**
     * Complete notification SOP task
     */
    const completeNotificationTask = async () => {
      try {
        const response = await postDataWithBody(
          completeNotificationSop,
          {
            sop_instance_id: id,
            task_id: task_id,
            user_name: getLoggedInUserName(),
          },
          {}
        );
        
        if (response.status === "success") {
          fetchTaskList();
          toast.success(response.msg, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Error completing notification task:", error);
        toast.error("Failed to complete task");
      }
      setShowNotificationConfirm(false);
    };

    /**
     * Complete activity task
     */
    const completeActivityTask = async () => {
      try {
        const todayDate = new Date().toISOString().split("T")[0];
        const response = await postDataWithBody(
          completeMytask,
          { id, taskCompletedDate: todayDate },
          {}
        );
        
        if (response.status === "success") {
          fetchTaskList();
          toast.success(response.msg, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Error completing activity task:", error);
        toast.error("Failed to complete task");
      }
      setShowActivityConfirm(false);
    };

    /**
     * Get dropdown background color based on status
     */
    const getStatusColor = (statusCode) => {
      const colors = {
        "TASK_EXE_STATUS_NEW_TASK": "#b2eafe",
        "TASK_EXE_STATUS_INPROGRESS": "#fccd7c",
        "TASK_EXE_STATUS_COMPLETED": "#7cdb90",
        "TASK_EXE_STATUS_CANCEL": "#e95060",
        "TASK_EXE_STATUS_CLOSED": "#e95060",
      };
      return colors[statusCode] || "gray";
    };

    // Dropdown styling
    const selectStyle = {
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      padding: "5px 20px 5px 5px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      backgroundColor: getStatusColor(status),
      color: "black",
      width: "120px",
      cursor: "pointer"
    };

    const wrapperStyle = {
      position: "relative",
      display: "inline-block"
    };

    const arrowStyle = {
      position: "absolute",
      right: "8px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "0",
      height: "0",
      borderLeft: "5px solid transparent",
      borderRight: "5px solid transparent",
      borderTop: "5px solid black",
      pointerEvents: "none"
    };

    // Handle special status cases
    if (status === "TASK_EXE_STATUS_CANCEL") {
      return (
        <div className="startedbysomeoneNew">
          <div>{t("Cancelled")}</div>
        </div>
      );
    }

    if (status === "TASK_EXE_STATUS_CLOSED") {
      return (
        <div className="startedbysomeoneNews">
          <div>{t("Closed")}</div>
        </div>
      );
    }

    return (
      <div>
        <div style={wrapperStyle}>
          <select
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
            style={selectStyle}
          >
            <option
              value="TASK_EXE_STATUS_NEW_TASK"
              disabled={status !== "TASK_EXE_STATUS_NEW_TASK"}
            >
              {task_type === "Notification" ? t("New Message") : t("Started")}
            </option>
            
            {task_type !== "Notification" && (
              <option
                value="TASK_EXE_STATUS_INPROGRESS"
                disabled={status === "TASK_EXE_STATUS_COMPLETED"}
              >
                {t("In Progress")}
              </option>
            )}
            
            <option
              value="TASK_EXE_STATUS_COMPLETED"
              disabled={
                task_type === "Notification"
                  ? status !== "TASK_EXE_STATUS_NEW_TASK"
                  : status !== "TASK_EXE_STATUS_INPROGRESS"
              }
            >
              {task_type === "Notification" ? t("Acknowledged") : t("Completed")}
            </option>
          </select>
          <div style={arrowStyle}></div>
        </div>

        {/* Confirmation popups */}
        {showNotificationConfirm && (
          <Confirmpopup
            show={showNotificationConfirm}
            title={t("acknowldgtask")}
            message={t("popupmsg")}
            handleToggle={() => setShowNotificationConfirm(false)}
            row={row}
            name={row.original.task_name}
            deleteRow={completeNotificationTask}
            cancel={t("cancelbutton")}
            action={t("confirm")}
          />
        )}

        {showActivityConfirm && (
          <Confirmpopup
            show={showActivityConfirm}
            title={t("acknowldgtask")}
            message="Are you sure you want to complete the task?"
            handleToggle={() => setShowActivityConfirm(false)}
            row={row}
            name={row.original.task_name}
            deleteRow={completeActivityTask}
            cancel={t("cancelbutton")}
            action={t("confirm")}
          />
        )}
      </div>
    );
  };

  /**
   * Component for displaying task priority with colored indicator
   */
  const PriorityCell = ({ value }) => {
    const priorityMap = {
      0: { color: "#b2eafe", label: t("Low") },
      1: { color: "#fccd7c", label: t("Medium") },
      2: { color: "#f0a4ac", label: t("High") },
    };

    const priority = priorityMap[value] || priorityMap[0];

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span
          style={{
            width: "10px",
            height: "10px",
            backgroundColor: priority.color,
            borderRadius: "50%",
            display: "inline-block",
          }}
        />
        <span>{priority.label}</span>
      </div>
    );
  };

  /**
   * Component for displaying file attachment link
   */
  const FileAttachmentCell = ({ value }) => {
    return value ? (
      <Paperclip
        onClick={() => handleImageClick(value)}
        style={{ cursor: 'pointer', color: '#1a7a73' }}
        size={18}
        strokeWidth={1.5}
      />
    ) : (
      "NA"
    );
  };

  /**
   * Component for displaying formatted date
   */
  const DateCell = ({ value }) => {
    if (!value) return "NA";
    
    const dateValue = new Date(value);
    return <Moment format="DD-MM-YYYY, hh:mm A">{dateValue}</Moment>;
  };

  /**
   * Table column definitions
   */
  const columns = React.useMemo(
    () => [
      {
        Header: t("soprelatedtasktableheaders.taskname"),
        accessor: "task_name",
        disableSortBy: true,
      },
      {
        Header: t("soprelatedtasktableheaders.actionmessage"),
        accessor: "action_message",
        Cell: ({ value }) => (
          <span title={value}>{value || "NA"}</span>
        ),
        disableSortBy: true,
      },
      {
        Header: t("soprelatedtasktableheaders.tasktype"),
        accessor: "task_type",
        disableSortBy: true,
      },
      {
        Header: t("soprelatedtasktableheaders.createdby"),
        accessor: "created_by",
        disableSortBy: true,
      },
      {
        Header: t("soprelatedtasktableheaders.createdon"),
        accessor: "created_on",
        Cell: ({ value }) => <DateCell value={value} />,
        disableSortBy: true,
      },
      {
        Header: t("soprelatedtasktableheaders.updatedon"),
        accessor: "updated_on",
        Cell: ({ value }) => <DateCell value={value} />,
        disableSortBy: true,
      },
      {
        Header: t("soprelatedtasktableheaders.Viewimage"),
        accessor: "task_file_name",
        disableSortBy: true,
        Cell: ({ value }) => <FileAttachmentCell value={value} />,
      },
      {
        Header: t("dailyoperationaltasktableheaders.status"),
        accessor: "task_status_code",
        disableSortBy: true,
        Cell: ({ row }) => <TaskStatusCell row={row} />,
      },
      {
        Header: t("soprelatedtasktableheaders.priority"),
        accessor: "priority",
        disableSortBy: true,
        Cell: ({ value }) => <PriorityCell value={value} />,
      },
      {
        Header: t("soprelatedtasktableheaders.cancelreason"),
        accessor: "cancel_reason",
        disableSortBy: true,
      },
    ],
    [t]
  );

  return (
    <div>
      {/* Task completion modal */}
      {showTaskModal && (
        <TaskModalWithCommentSop
          titleValue={t("actiontaken")}
          showSuccessMsg={handleTaskCompletion}
          taskData={taskData}
          show={showTaskModal}
          buttonLabel={t("popup.save")}
          handleToggle={showTaskCompletionModal}
          getMyTaskList={fetchTaskList}
          setShowPop={setShowTaskModal}
          {...props}
        />
      )}

      {/* Main task table */}
      {facilityTableData && (
        <Table
          data={data}
          title=""
          columns={columns}
          numberOfRows={7}
          showExport={false}
          showReset={false}
          onExpand={false}
          showSearch={true}
          isHeaderFixed={true}
          rowCount={rowCount}
          headerTableHeadStyle={{ 
            background: "#1a7a73", 
            color: "#FFFFFF" 
          }}
          tableStyle={{
            border: "1px solid #f0f0f0",
            padding: "2px",
            borderRadius: "12px",
          }}
        />
      )}

      {/* Image preview modal */}
      <Modal 
        show={showImageModal} 
        onHide={handleImageModalClose} 
        centered 
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center' }}>
          <img
            src={imageSrc}
            alt="Preview"
            style={{ 
              maxWidth: '100%', 
              maxHeight: '70vh', 
              objectFit: 'contain' 
            }}
          />
        </Modal.Body>
      </Modal>

      {/* Toast notifications */}
      <ToastContainer />
    </div>
  );
});

export default MyTaskList;